import { zodResolver } from "@hookform/resolvers/zod";
import {
  KdsButton,
  KdsHeader,
  KdsModal,
  KdsText,
} from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { includes } from "lodash";
import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Controller,
  FormProvider,
  type Resolver,
  type SubmitHandler,
  useForm,
} from "react-hook-form";
import { Navigate } from "react-router-dom";
import { PermissionItem } from "@/api/__generated__/orval/auth/index.ts";
import { PageTemplate } from "@/components/PageTemplate";
import { FeatureFlagUpsertCommandFeatureEnum } from "@/config/apiClient/api.ts";
import { useFeatureFlags, useToasts } from "@/hooks";
import { useRolePermission } from "@/hooks/rolePermission/useRolePermission.ts";
import { SwitchGroup } from "./components/SwitchGroup.tsx";
import {
  type FeatureFlagFormInputs,
  featureFlagSchema,
} from "./schema/featureFlagSchema.ts";
import {
  FLAG_WITH_EXTRA_DATA,
  getMetadataByFeature,
} from "./utils/featureFlagWithExtraData";

export const SettingsFormPage = () => {
  // Hooks
  const { t } = useTranslate();
  const { add } = useToasts();

  const { userHasPermission } = useRolePermission();
  // States
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  // Queries
  const { useGetFeatureFlags, useCreateOrUpdateFeatureFlags } =
    useFeatureFlags();
  const { data: featureFlagsData, isPending: isGettingFeatureFlags } =
    useGetFeatureFlags();
  const { mutateAsync: createOrUpdateFeatureFlags, isPending } =
    useCreateOrUpdateFeatureFlags();

  const translationFromFeatureTitle = (
    feature: FeatureFlagUpsertCommandFeatureEnum
  ) => {
    switch (feature) {
      case FeatureFlagUpsertCommandFeatureEnum.Reporting:
        return t("oms.featureFlags.REPORTING.title");
      case FeatureFlagUpsertCommandFeatureEnum.PurchaseOrder:
        return t("oms.featureFlags.PURCHASE_ORDER.title");
      case FeatureFlagUpsertCommandFeatureEnum.PreMedia:
        return t("oms.featureFlags.PRE_MEDIA.title");
      case FeatureFlagUpsertCommandFeatureEnum.Retroplanning:
        return t("oms.featureFlags.RETROPLANNING.title");
      case FeatureFlagUpsertCommandFeatureEnum.WorkflowsWithoutQuoteForPricedProducts:
        return t(
          "oms.featureFlags.WORKFLOWS_WITHOUT_QUOTE_FOR_PRICED_PRODUCTS.title"
        );
      case FeatureFlagUpsertCommandFeatureEnum.DeliveryLabel:
        return t("oms.featureFlags.DELIVERY_LABEL.title");
      case FeatureFlagUpsertCommandFeatureEnum.Mymarket:
        return t("oms.featureFlags.MY_MARKET.title");
      case FeatureFlagUpsertCommandFeatureEnum.ImportExternalOrders:
        return t("oms.featureFlags.IMPORT_EXTERNAL_ORDERS.title");
      default:
        return "";
    }
  };

  const translationFromFeatureDescription = (
    feature: FeatureFlagUpsertCommandFeatureEnum
  ) => {
    switch (feature) {
      case FeatureFlagUpsertCommandFeatureEnum.Reporting:
        return t("oms.featureFlags.REPORTING.description");
      case FeatureFlagUpsertCommandFeatureEnum.PurchaseOrder:
        return t("oms.featureFlags.PURCHASE_ORDER.description");
      case FeatureFlagUpsertCommandFeatureEnum.PreMedia:
        return t("oms.featureFlags.PRE_MEDIA.description");
      case FeatureFlagUpsertCommandFeatureEnum.Retroplanning:
        return t("oms.featureFlags.RETROPLANNING.description");
      case FeatureFlagUpsertCommandFeatureEnum.WorkflowsWithoutQuoteForPricedProducts:
        return t(
          "oms.featureFlags.WORKFLOWS_WITHOUT_QUOTE_FOR_PRICED_PRODUCTS.description"
        );
      case FeatureFlagUpsertCommandFeatureEnum.DeliveryLabel:
        return t("oms.featureFlags.DELIVERY_LABEL.description");
      case FeatureFlagUpsertCommandFeatureEnum.Mymarket:
        return t("oms.featureFlags.MY_MARKET.description");
      case FeatureFlagUpsertCommandFeatureEnum.ImportExternalOrders:
        return t("oms.featureFlags.IMPORT_EXTERNAL_ORDERS.description");
      default:
        return "";
    }
  };
  // Create feature flags array
  const featureFlags = useMemo(() => {
    return (
      Object.values(FeatureFlagUpsertCommandFeatureEnum)
        .map((feature) => ({
          feature,
          title: translationFromFeatureTitle(feature),
          description: translationFromFeatureDescription(feature),
          isActive: Boolean(
            featureFlagsData?.data?.find(
              (featureFlag) => featureFlag.feature === feature
            )?.isActive
          ),
          metadata:
            featureFlagsData?.data?.find(
              (featureFlag) => featureFlag.feature === feature
            )?.metadata || {},
        }))
        // TODO : remove these features flags when backend is updated
        .filter(
          (flag) =>
            flag.feature !== FeatureFlagUpsertCommandFeatureEnum.OpenBrief &&
            flag.feature !== FeatureFlagUpsertCommandFeatureEnum.Dashboard
        )
    );
  }, [featureFlagsData, t]);

  // Form creation
  const methods = useForm<FeatureFlagFormInputs>({
    resolver: zodResolver(featureFlagSchema) as Resolver<FeatureFlagFormInputs>,
    defaultValues: {
      featureFlags: [],
    },
  });
  const {
    formState: { isDirty },
  } = methods;

  useEffect(() => {
    if (isGettingFeatureFlags) {
      return;
    }
    methods.reset({
      featureFlags: featureFlags.map((flag) => ({
        feature: flag.feature,
        isActive: flag.isActive,
        metadata: flag.metadata,
      })),
    });
  }, [isGettingFeatureFlags, featureFlags, methods]);

  const onSubmit: SubmitHandler<FeatureFlagFormInputs> = async (data) => {
    const res = await createOrUpdateFeatureFlags(data.featureFlags);

    if (res.status === 200 || res.status === 201) {
      add({
        state: "success",
        title: t("oms.featureFlags.callout.success"),
      });
      methods.reset(data);
      setIsConfirmModalOpen(false);
    } else {
      console.error("error");
      add({
        state: "error",
        title: t("oms.featureFlags.callout.error"),
      });
    }
  };

  const handleConfirm = async () => {
    const isValid = await methods.trigger();
    if (!isValid) {
      return;
    }
    setIsConfirmModalOpen(true);
  };

  const productionFeatureFlagsName: FeatureFlagUpsertCommandFeatureEnum[] = [
    FeatureFlagUpsertCommandFeatureEnum.PurchaseOrder,
    FeatureFlagUpsertCommandFeatureEnum.PreMedia,
    FeatureFlagUpsertCommandFeatureEnum.Retroplanning,
    FeatureFlagUpsertCommandFeatureEnum.DeliveryLabel,
  ];

  // Split features in two groups (demo and prod)
  const { productionFeatures, demoFeatures } = useMemo(() => {
    return featureFlags.reduce<{
      productionFeatures: typeof featureFlags;
      demoFeatures: typeof featureFlags;
    }>(
      (acc, flag) => {
        if (productionFeatureFlagsName.includes(flag.feature)) {
          acc.productionFeatures.push(flag);
        } else {
          acc.demoFeatures.push(flag);
        }
        return acc;
      },
      { productionFeatures: [], demoFeatures: [] }
    );
  }, [featureFlags, productionFeatureFlagsName.includes]);

  if (!userHasPermission(PermissionItem.kazaaradminmanagement)) {
    return (
      <Navigate
        to={`/403?missing_perms=${PermissionItem.kazaaradminmanagement}`}
        replace
      />
    );
  }

  return (
    <PageTemplate dataCy="csmOrdersPage">
      <PageTemplate.Header>
        <KdsHeader title={t("oms.featureFlags.title")} />
      </PageTemplate.Header>

      {!isGettingFeatureFlags && (
        <PageTemplate.Content className="flex flex-col gap-4 self-stretch">
          <form
            className="flex w-full flex-col gap-4 pb-4"
            onSubmit={(e) => void methods.handleSubmit(onSubmit)(e)}
          >
            <div className="flex flex-col items-start gap-6 self-stretch rounded-2xl bg-white p-6">
              <KdsText size="subtitle-sm">
                {t("oms.featureFlags.subtitle")}
              </KdsText>

              {/* Production Features */}
              <KdsText size="subtitle-sm">
                {t("oms.featureFlags.productionFeaturesTitle")}
              </KdsText>

              {productionFeatures.map((flag) => {
                const hasMetadata = includes(
                  FLAG_WITH_EXTRA_DATA,
                  flag.feature
                );
                const originalIndex = featureFlags.findIndex(
                  (f) => f.feature === flag.feature
                );

                return (
                  <Fragment key={flag.feature}>
                    <Controller
                      key={`${flag.feature}-feature`}
                      name={`featureFlags.${originalIndex}.feature`}
                      control={methods.control}
                      defaultValue={flag.feature}
                      render={({ field }) => <input type="hidden" {...field} />}
                    />
                    <Controller
                      key={`${flag.feature}-metadata`}
                      name={`featureFlags.${originalIndex}.metadata`}
                      control={methods.control}
                      defaultValue={flag.metadata}
                      render={() => <></>}
                    />
                    <Controller
                      key={flag.feature}
                      name={`featureFlags.${originalIndex}.isActive`}
                      control={methods.control}
                      render={({ field }) => (
                        <FormProvider {...methods}>
                          <SwitchGroup
                            isSelected={!!field.value}
                            title={flag.title}
                            description={flag.description}
                            onChange={field.onChange}
                            withMetadata={hasMetadata}
                            metadataComponent={getMetadataByFeature(
                              flag.feature,
                              originalIndex
                            )}
                          />
                        </FormProvider>
                      )}
                    />
                  </Fragment>
                );
              })}

              {/* Demo Features */}
              <KdsText size="subtitle-sm">
                {t("oms.featureFlags.demoFeaturesTitle")}
              </KdsText>

              {demoFeatures.map((flag) => {
                const hasMetadata = includes(
                  FLAG_WITH_EXTRA_DATA,
                  flag.feature
                );
                const originalIndex = featureFlags.findIndex(
                  (f) => f.feature === flag.feature
                );

                return (
                  <Fragment key={flag.feature}>
                    <Controller
                      key={`${flag.feature}-feature`}
                      name={`featureFlags.${originalIndex}.feature`}
                      control={methods.control}
                      defaultValue={flag.feature}
                      render={({ field }) => <input type="hidden" {...field} />}
                    />
                    <Controller
                      key={`${flag.feature}-metadata`}
                      name={`featureFlags.${originalIndex}.metadata`}
                      control={methods.control}
                      defaultValue={flag.metadata}
                      render={() => <></>}
                    />
                    <Controller
                      key={flag.feature}
                      name={`featureFlags.${originalIndex}.isActive`}
                      control={methods.control}
                      render={({ field }) => (
                        <FormProvider {...methods}>
                          <SwitchGroup
                            isSelected={!!field.value}
                            title={flag.title}
                            description={flag.description}
                            onChange={field.onChange}
                            withMetadata={hasMetadata}
                            metadataComponent={getMetadataByFeature(
                              flag.feature,
                              originalIndex
                            )}
                          />
                        </FormProvider>
                      )}
                    />
                  </Fragment>
                );
              })}
            </div>

            <div className="inline-flex items-end justify-end gap-4">
              <KdsButton
                size="large"
                variant="outlined"
                color="neutral"
                onPress={() => methods.reset()}
              >
                {t("oms.featureFlags.buttons.cancel")}
              </KdsButton>
              <KdsButton
                size="large"
                dataCy="featureFlagModificationButton"
                onPress={() => void handleConfirm()}
                disabled={isGettingFeatureFlags || !isDirty}
              >
                {t("oms.featureFlags.buttons.modify")}
              </KdsButton>
            </div>

            <KdsModal
              isOpen={isConfirmModalOpen}
              onOpenChange={setIsConfirmModalOpen}
              size="sm"
              dialogProps={{
                dialogHeaderProps: {
                  title: t("oms.featureFlags.modal.title"),
                },
                content: (
                  <KdsText>{t("oms.featureFlags.modal.description")}</KdsText>
                ),
                dialogFooterProps: {
                  buttonProps: [
                    {
                      children: t("oms.featureFlags.modal.buttons.cancel"),
                      variant: "ghost",
                      color: "neutral",
                      onPress: () => setIsConfirmModalOpen(false),
                      isLoading: isPending,
                    },
                    {
                      children: t("oms.featureFlags.modal.buttons.confirm"),
                      isLoading: isPending,
                      onPress: () => void methods.handleSubmit(onSubmit)(),
                      onKeyPress: (e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                        }
                      },
                    },
                  ],
                },
              }}
            />
          </form>
        </PageTemplate.Content>
      )}
    </PageTemplate>
  );
};
